import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import '../styles/UpdateOptions.css';
import ImageUploadField from './ReusableComponent/ImageUploadField';
import RichTextEditor from './ReusableComponent/RichTextEditor';
import { isHtmlEmpty, useHighlightCode } from './utils/utility';

const UpdateOptionsTable = ({ questionId, openDialogExternally, setOpenDialogExternally, handleOptionsChange, correctIds = [], onCorrectChange, onOptionsLoaded }) => {
  const [options, setOptions] = useState([]);
  const [option, setOption] = useState({ option: '', optionAttachment: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editingOptionId, setEditingOptionId] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const optionsTableRef = useRef(null);
  useHighlightCode(optionsTableRef, [options]);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (openDialogExternally) {
      setShowDialog(true);
      setOpenDialogExternally(false); // Reset parent signal
    }
  }, [openDialogExternally, setOpenDialogExternally]);

  const validateOption = () => {
    const errors = {};
    if (isHtmlEmpty(option.option) && !option.optionAttachment)
      errors.option = 'Option text or image is required';
    return errors;
  };

  const fetchOptions = () => {
    if (!questionId) {
      setOptions([]);
      return;
    }

    axios
      .get(`${API_URL}/api/options/${questionId}`)
      .then((res) => {
        const response = Array.isArray(res.data) ? res.data : res.data.options || [];
        setOptions(response);
        if (onOptionsLoaded) onOptionsLoaded(response);
      })
      .catch((err) => {
        console.error('Error fetching options:', err);
        setOptions([]);
      });
  };

  useEffect(() => {
    const load = async () => {
      if (!questionId) {
        setOptions([]);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/options/${questionId}`);
        const response = Array.isArray(res.data) ? res.data : res.data.options || [];
        setOptions(response);
        if (onOptionsLoaded) onOptionsLoaded(response);
      } catch (err) {
        console.error('Error fetching options:', err);
        setOptions([]);
      }
    };
    load();
  }, [questionId, API_URL]);

  const handleAddOrUpdateOption = async () => {
    if(!isEditing) {
      const validationErrors = validateOption();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
    }

    if (isHtmlEmpty(option.option) && !option.optionAttachment) {
      setShowPopup(true);
      return;
    }


    try {
      const optionData = {
        ...option,
        questionId,
        status: 'Enabled',
        dateTime: new Date().toISOString(),
      };

      if (isEditing && editingOptionId) {
        await axios.put(`${API_URL}/api/options/${editingOptionId}`, optionData);
      } else {
        await axios.post(`${API_URL}/api/options`, optionData);
      }

      fetchOptions();
      if (handleOptionsChange) handleOptionsChange();
    } catch (err) {
      console.error('Error saving option:', err);
    }

    setOption({ option: '', optionAttachment: '' });
    setIsEditing(false);
    setEditingOptionId(null);
    setErrors({});
    setShowDialog(false);
  };


  const handleToggleCorrect = (optionId, e) => {
    e.stopPropagation();
    if (!onCorrectChange) return;
    const id = optionId.toString();
    const newIds = correctIds.includes(id)
      ? correctIds.filter((c) => c !== id)
      : [...correctIds, id];
    onCorrectChange(newIds);
  };

  const handleRowClick = (optionData) => {
    setOption({
      option: optionData.option,
      optionAttachment: optionData.optionAttachment,
    });
    setIsEditing(true);
    setEditingOptionId(optionData._id);
    setShowDialog(true);
  };

  const handleDeleteOption = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteOption = async () => {
    if (!editingOptionId) return;
    try {
      await axios.delete(`${API_URL}/api/options/${editingOptionId}`);
      fetchOptions();
    } catch (err) {
      console.error('Error deleting option:', err);
    }
    handleCancel();
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleCancel = () => {
    setOption({ option: '', optionAttachment: '' });
    setIsEditing(false);
    setEditingOptionId(null);
    setErrors({});
    setShowDialog(false);
  };

  return (
    <div className="option-table-wrapper">
      {options.length > 0 && (<div className="option-table-header">
        <h3>Options</h3>
      </div>
      )}


      {showDialog && (
        <div className="dialog-backdrop">
          <div className="dialog-box">
            <button className="option-modal-close" onClick={handleCancel}>
              ×
            </button>
            <h4>{isEditing ? 'Edit Option' : 'Add Option'}</h4>

            <div className="option-form-group">
              <label htmlFor="option">Option</label>
              <RichTextEditor
                toolbar="basic"
                height={120}
                value={option.option}
                onChange={(html) => {
                  setOption((prev) => ({ ...prev, option: html }));
                  setErrors((prev) => ({ ...prev, option: '' }));
                }}
                placeholder="Enter your option"
              />
              {errors.option && <div className="error-text">{errors.option}</div>}
            </div>

            <div className="option-form-group">
              <label htmlFor="optionAttachment">Option Image (optional)</label>
              <ImageUploadField
                value={option.optionAttachment}
                onChange={(url) => setOption((prev) => ({ ...prev, optionAttachment: url }))}
              />
            </div>

            <div className="dialog-actions">
              {isEditing && (
                <button type="button" className="update-delete-btn" onClick={handleDeleteOption}>
                  Delete
                </button>
              )}
              <button type="button" className="update-btn" onClick={handleAddOrUpdateOption}>
                {isEditing ? 'Update Option' : 'Save Option'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPopup && (
        <div className="popup-backdrop">
          <div className="popup-box">
            <p>Please enter option text or upload an image before saving.</p>
            <button onClick={() => setShowPopup(false)}>OK</button>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="popup-backdrop">
          <div className="popup-box delete-confirm">
            <p>Are you sure you want to delete this option?</p>
            <div className="delete-confirm-actions">
              <button type='button' className="btn-cancel" onClick={cancelDelete}>Cancel</button>
              <button type='button' className="btn-ok" onClick={confirmDeleteOption}>OK</button>
            </div>
          </div>
        </div>
      )}

      {options.length > 0 && <table className="note-table" ref={optionsTableRef}>
        <thead>
          <tr>
            {onCorrectChange && <th style={{ width: 80 }}>Correct</th>}
            <th>Option</th>
            <th>Option Attachment</th>
          </tr>
        </thead>
        <tbody>
          {options.map((item) => (
            <tr key={item._id} onClick={() => handleRowClick(item)} style={{ cursor: "pointer" }}>
              {onCorrectChange && (
                <td style={{ textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={correctIds.includes(item._id.toString())}
                    onChange={(e) => handleToggleCorrect(item._id, e)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                  />
                </td>
              )}
              <td>
                {item.option
                  ? <span dangerouslySetInnerHTML={{ __html: item.option.replace(/src="\/uploads\//g, `src="${process.env.REACT_APP_API_URL}/uploads/`) }} />
                  : "—"}
              </td>
              <td>
                {item.optionAttachment ? (
                  <img
                    src={item.optionAttachment.startsWith("/uploads/")
                      ? `${process.env.REACT_APP_API_URL}${item.optionAttachment}`
                      : item.optionAttachment}
                    alt="option"
                    style={{ width: 60, height: 40, objectFit: "contain", borderRadius: 4, border: "1px solid #e5e7eb" }}
                  />
                ) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      }
    </div>
  );
};

export default UpdateOptionsTable;
